/* ==========================================================================
   COVE PRO — page behaviour
   Reveal-on-scroll, sticky chapter nav, scroll spy, carousels, video
   autoplay-in-view and animated stats. Vanilla, dependency-free, idempotent
   (safe to load twice and safe inside the Shopify theme editor).
   ========================================================================== */
(() => {
  if (window.__covePro) return;
  window.__covePro = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const supportsIO = 'IntersectionObserver' in window;

  /* -------------------------------------------------------------------- */
  /* Reveal on scroll                                                       */
  /* -------------------------------------------------------------------- */
  let revealObserver = null;

  const initReveal = () => {
    const targets = $$('[data-cove-reveal]:not([data-cove-ready])');
    if (!targets.length) return;

    if (reduceMotion.matches || !supportsIO) {
      targets.forEach((el) => {
        el.dataset.coveReady = '1';
        el.classList.add('is-visible');
      });
      return;
    }

    revealObserver ||= new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        revealObserver.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });

    targets.forEach((el) => {
      el.dataset.coveReady = '1';
      // Anything already in view on load shows immediately (no pop-in).
      const rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.92 && rect.bottom > 0) {
        el.classList.add('is-visible');
      } else {
        revealObserver.observe(el);
      }
    });
  };

  /* -------------------------------------------------------------------- */
  /* Animated numbers ("5.000", "15 W", "1,5×")                            */
  /* -------------------------------------------------------------------- */
  const animateNumber = (el) => {
    const source = el.dataset.coveCount || el.textContent.trim();
    const match = source.match(/-?\d[\d.\s]*(?:,\d+)?|-?\d+(?:\.\d+)?/);
    if (!match) return;

    const raw = match[0].replace(/\s+$/, '');
    const prefix = source.slice(0, match.index);
    const suffix = source.slice(match.index + raw.length);
    const usesCommaDecimal = raw.includes(',');
    const normalized = usesCommaDecimal ? raw.replace(/[.\s]/g, '').replace(',', '.') : raw.replace(/\s/g, '').replace(/\.(?=\d{3}(?:\D|$))/g, '');
    const target = parseFloat(normalized);
    if (Number.isNaN(target)) return;

    const decimals = usesCommaDecimal ? (raw.split(',')[1] || '').length : (raw.includes('.') && !/\.\d{3}(?:\D|$)/.test(raw) ? (raw.split('.')[1] || '').length : 0);
    const locale = document.documentElement.lang || 'de';
    const formatter = new Intl.NumberFormat(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    const duration = 1400;
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      el.textContent = `${prefix}${formatter.format(target * eased)}${suffix}`;
      if (progress < 1) requestAnimationFrame(tick);
      else el.textContent = source;
    };

    requestAnimationFrame(tick);
  };

  const initCounters = () => {
    const targets = $$('[data-cove-count]:not([data-cove-ready])');
    if (!targets.length) return;

    if (reduceMotion.matches || !supportsIO) {
      targets.forEach((el) => { el.dataset.coveReady = '1'; });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animateNumber(entry.target);
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.4 });

    targets.forEach((el) => {
      el.dataset.coveReady = '1';
      observer.observe(el);
    });
  };

  /* -------------------------------------------------------------------- */
  /* Rotating headline word ("Laptop" → "iPad" → …)                        */
  /* Cycles only while on screen and the tab is visible. The stack width    */
  /* tracks the current word so the static tail glides, while the outer box */
  /* is locked to the widest variant so the headline never re-wraps.        */
  /* -------------------------------------------------------------------- */
  const initRotators = () => {
    $$('[data-cove-rotate]:not([data-cove-ready])').forEach((el) => {
      el.dataset.coveReady = '1';
      const words = $$('.cove-rotate__word', el);
      if (words.length < 2) return;

      const stack = el.querySelector('.cove-rotate__stack') || el;
      const tail = el.querySelector('.cove-rotate__tail');
      const interval = Math.max(1000, parseInt(el.dataset.coveRotateInterval, 10) || 2500);
      let index = Math.max(0, words.findIndex((w) => w.classList.contains('is-current')));
      let timer = null;

      const fit = () => {
        // offsetWidth is the layout box, unaffected by the 3D pose of the
        // faces mid-flip (a client rect would be skewed by the perspective).
        const widths = words.map((w) => w.offsetWidth);
        const tailWidth = tail ? tail.offsetWidth : 0;
        el.style.width = `${Math.max(...widths) + tailWidth}px`;
        stack.style.width = `${widths[index]}px`;
      };

      const step = () => {
        if (document.hidden) return;
        const current = words[index];
        index = (index + 1) % words.length;
        const next = words[index];

        current.classList.remove('is-current');
        current.classList.add('is-leaving');
        current.setAttribute('aria-hidden', 'true');
        next.classList.remove('is-leaving');
        next.classList.add('is-current');
        next.removeAttribute('aria-hidden');
        fit();

        // Let the leaving face finish, then park it back at the start pose.
        setTimeout(() => current.classList.remove('is-leaving'), 750);
      };

      const start = () => { timer ||= setInterval(step, interval); };
      const stop = () => { clearInterval(timer); timer = null; };

      fit();
      document.fonts?.ready.then(fit);
      window.addEventListener('resize', fit, { passive: true });

      if (supportsIO) {
        new IntersectionObserver((entries) => {
          entries.forEach((entry) => (entry.isIntersecting ? start() : stop()));
        }, { threshold: 0.1 }).observe(el);
      } else {
        start();
      }

      el.__coveRotateStop = stop;
    });
  };

  /* -------------------------------------------------------------------- */
  /* Videos: play when visible, pause when not. "once" = play then hold.   */
  /* -------------------------------------------------------------------- */
  const initVideos = () => {
    const videos = $$('video[data-cove-video]:not([data-cove-ready]), [data-cove-video-wrap] video:not([data-cove-ready])');
    if (!videos.length) return;

    const play = (video) => {
      const attempt = video.play();
      if (attempt && attempt.catch) attempt.catch(() => {});
    };

    videos.forEach((video) => {
      video.dataset.coveReady = '1';
      video.muted = true;
      video.setAttribute('playsinline', '');

      if (reduceMotion.matches) {
        video.pause();
        video.removeAttribute('autoplay');
        return;
      }

      if (!supportsIO) {
        play(video);
        return;
      }

      const mode = video.dataset.coveVideo || video.closest('[data-cove-video-wrap]')?.dataset.coveVideoWrap || 'loop';
      let finished = false;
      video.addEventListener('ended', () => { finished = true; });

      const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (finished && mode === 'once') {
              if (video.dataset.coveReplay !== 'false') {
                video.currentTime = 0;
                finished = false;
                play(video);
              }
              return;
            }
            play(video);
          } else if (!video.paused) {
            video.pause();
          }
        });
      }, { threshold: 0.35 });

      observer.observe(video);
    });
  };

  /* -------------------------------------------------------------------- */
  /* Smooth anchor scrolling that respects the sticky chapter nav          */
  /* -------------------------------------------------------------------- */
  // Scrolling down hides the theme header (so only the chapter nav remains on
  // top); scrolling up reveals it again, so include its current height then.
  const navOffset = (goingDown) => {
    const nav = document.querySelector('.cove-nav');
    if (!nav) return 0;
    const rect = nav.getBoundingClientRect();
    return rect.height + (goingDown ? 0 : Math.max(0, rect.top));
  };

  document.addEventListener('click', (event) => {
    const anchor = event.target.closest('a[data-cove-anchor]');
    if (!anchor) return;
    const hash = anchor.getAttribute('href');
    if (!hash || !hash.startsWith('#')) return;
    const target = document.querySelector(hash);
    if (!target) return;

    event.preventDefault();
    const targetTop = target.getBoundingClientRect().top + window.scrollY;
    const top = targetTop - navOffset(targetTop > window.scrollY) - 8;
    window.scrollTo({ top: Math.max(0, top), behavior: reduceMotion.matches ? 'auto' : 'smooth' });
    if (history.replaceState) history.replaceState(null, '', hash);
  });

  /* -------------------------------------------------------------------- */
  /* Chapter nav: sits below the theme header while visible, hugs the top  */
  /* once the header hides. Highlights the chapter in view.                */
  /* -------------------------------------------------------------------- */
  const initNav = (nav) => {
    if (nav.dataset.coveReady) return;
    nav.dataset.coveReady = '1';

    // Impact keeps the header *wrapper* sticky and slides / hides the inner
    // <store-header> on scroll, so measure the inner element when present.
    // Query in priority order (a comma list would return whichever comes first
    // in the DOM, e.g. the announcement bar).
    const header = ['.shopify-section--header', '#shopify-section-header', '.shopify-section-group-header-group']
      .map((selector) => document.querySelector(selector))
      .find(Boolean) || null;
    const headerInner = () => (header && (header.querySelector('store-header, .header') || header)) || null;
    const holder = nav.closest('.cove-nav-section') || nav;
    let frame = 0;

    const position = () => {
      frame = 0;
      let top = 0;
      if (header) {
        const wrapStyle = getComputedStyle(header);
        if (wrapStyle.position === 'sticky' || wrapStyle.position === 'fixed') {
          const inner = headerInner();
          const style = getComputedStyle(inner);
          if (style.visibility !== 'hidden' && style.opacity !== '0' && style.display !== 'none') {
            top = Math.max(0, Math.round(inner.getBoundingClientRect().bottom));
          }
        }
      }
      holder.style.setProperty('--cove-nav-top', `${top}px`);
    };

    // The theme header slides in/out with a transition after scrolling stops,
    // so keep measuring for a moment after the last scroll event.
    let settleUntil = 0;
    const loop = () => {
      position();
      if (performance.now() < settleUntil) frame = requestAnimationFrame(loop);
    };
    const schedule = () => {
      settleUntil = performance.now() + 900;
      if (!frame) frame = requestAnimationFrame(loop);
    };
    position();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    header?.addEventListener('transitionend', schedule);
    header?.addEventListener('transitionstart', schedule);
    if (header && 'ResizeObserver' in window) new ResizeObserver(schedule).observe(header);

    // Scroll spy
    const links = $$('.cove-nav__links a', nav);
    const sections = links.map((link) => document.querySelector(link.getAttribute('href'))).filter(Boolean);
    if (!sections.length || !supportsIO) return;

    const setActive = (id) => {
      links.forEach((link) => {
        if (link.getAttribute('href') === `#${id}`) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    };

    const spy = new IntersectionObserver((entries) => {
      const visible = entries.filter((e) => e.isIntersecting).sort((a, b) => b.intersectionRatio - a.intersectionRatio);
      if (visible.length) setActive(visible[0].target.id);
    }, { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.1, 0.25, 0.5] });

    sections.forEach((section) => spy.observe(section));
  };

  /* -------------------------------------------------------------------- */
  /* Horizontal card tracks with prev / next buttons                        */
  /* -------------------------------------------------------------------- */
  const initTracks = () => {
    $$('[data-cove-track]:not([data-cove-ready])').forEach((wrap) => {
      wrap.dataset.coveReady = '1';
      const track = wrap.querySelector('.cove-track');
      const prev = wrap.querySelector('[data-cove-prev]');
      const next = wrap.querySelector('[data-cove-next]');
      if (!track) return;

      const step = () => {
        const first = track.firstElementChild;
        if (!first) return track.clientWidth * 0.8;
        const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
        return first.getBoundingClientRect().width + gap;
      };

      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
      };

      prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion.matches ? 'auto' : 'smooth' }));
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      update();
    });
  };

  /* -------------------------------------------------------------------- */
  /* Boot                                                                   */
  /* -------------------------------------------------------------------- */
  const init = () => {
    initReveal();
    initCounters();
    initRotators();
    initVideos();
    initTracks();
    $$('.cove-nav').forEach(initNav);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:unload', (event) => {
    $$('[data-cove-rotate]', event.target).forEach((el) => el.__coveRotateStop?.());
  });
  document.addEventListener('shopify:block:select', (event) => {
    const el = event.target?.closest?.('[data-cove-reveal]');
    if (el) el.classList.add('is-visible');
  });
})();
